import { Div, Button } from '@codesuma/baseline'
import { CatalogCountry } from '../../services/occasion-catalog'
import { regionWords } from './region-text'
import style from './index.module.css'

// The state picker: which of a country's states the list below it covers.
//
// A field of pills, and a list that opens under it. Marking a state puts its
// holidays in the calendar there and then — the pill means "Delaware's holidays
// are mine", not "show me Delaware" — and a single row underneath can still be
// switched off afterwards.
//
// Nationwide days are the base of every list, so the first pill is a statement
// and not a control: it has no cross, and its row in the list has a tick that
// cannot be cleared. There is no way to ask for a state's holidays without the
// country's own.
//
// Only states that have a holiday of their own are offered. Seventeen of the
// fifty-two US regions have none, and the note at the foot of the list says so
// rather than leaving the reader hunting for Ohio.
//
// The word for what is being picked comes from the country — see regionWords in
// region-text.ts. Most of them are states; Britain's are nations and France's is
// an area, and calling either an ایالت is wrong.

const CLOSE_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>'
const CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 13l4 4L19 7"/></svg>'

// What the page is showing, and how to change it. The page owns this because
// the answer comes from the reader's subscription once there is one, and from
// the page's own opening state until then.
export interface IRegionSelection {
    all: () => boolean
    has: (region: string) => boolean
    set: (region: string, on: boolean) => void
    setAll: (on: boolean) => void
}

export const RegionPicker = (
    country: CatalogCountry,
    codes: string[],
    syncers: (() => void)[],
    selection: IRegionSelection,
) => {
    const nameOf = (region: string) => {
        const found = country.regions.find(r => r.code === region)
        return found?.names.fa ?? found?.names.en ?? region
    }
    // Germany and the US answer to a federal government; Britain, Australia and
    // France do not, and their nationwide days are just that.
    const baseName = country.researched.includes('federal') ? 'فدرال' : 'سراسری'
    const [one, many] = regionWords(country)

    const wrap = Div()
    wrap.addClass(style.picker)

    const field = Div()
    field.addClass(style.field)
    wrap.append(field)

    const basePill = Div(baseName)
    basePill.addClass(style.pill)
    basePill.addClass(style.basePill)
    field.append(basePill)

    // Rebuilt on every sync: which pills there are is the thing that changes.
    const pills = Div()
    pills.addClass(style.pills)
    field.append(pills)

    // Last, so it follows what has been picked rather than leading it: the
    // field is read as the states you have, and then the way to add another.
    const open = Button(`انتخاب ${one}`)
    open.addClass(style.open)
    field.append(open)

    const options = Div()
    options.addClass(style.options)
    wrap.append(options)

    let showing = false

    // A press anywhere else is a press past the list, so the list goes away.
    // Presses inside it are its own — the control that opens it is in there
    // too, so the tap that opened it is never the tap that closes it.
    const outside = (event: PointerEvent) => {
        if (!wrap.el.isConnected) {
            document.removeEventListener('pointerdown', outside)
            return
        }
        if (wrap.el.contains(event.target as Node)) return
        showing = false
        options.toggleClass(style.showing, false)
        document.removeEventListener('pointerdown', outside)
    }

    open.on('click', () => {
        showing = !showing
        options.toggleClass(style.showing, showing)
        // click lands after the press that carried it, so this listener never
        // hears the one that just opened the list.
        if (showing) document.addEventListener('pointerdown', outside)
        else document.removeEventListener('pointerdown', outside)
    })

    const pill = (text: string, drop: () => void) => {
        const button = Button()
        button.addClass(style.pill)
        button.append(Div(text))
        const cross = Div()
        cross.addClass(style.pillCross)
        cross.html(CLOSE_SVG)
        button.append(cross)
        button.attr('aria-label', `حذف ${text}`)
        button.on('click', drop)
        pills.append(button)
    }

    syncers.push(() => {
        pills.empty()
        // Every state is one thing to say, not thirty-five pills of it.
        if (selection.all()) pill(`همهٔ ${many}`, () => selection.setAll(false))
        else {
            for (const region of codes) {
                if (selection.has(region)) pill(nameOf(region), () => selection.set(region, false))
            }
        }
        // The first ask is for a state; once there is one, it is for another.
        open.el.textContent = pills.el.childElementCount
            ? `انتخاب ${one} دیگر`
            : `انتخاب ${one}`
    })

    // The nationwide row, first and always ticked. Not a button: there is
    // nothing to press, and a control that cannot do anything is worse than
    // none at all.
    const base = Div()
    base.addClass(style.baseOption)
    const baseMark = Div()
    baseMark.addClass(style.optionMark)
    baseMark.html(CHECK_SVG)
    base.append(baseMark)
    base.append(Div(baseName))
    options.append(base)

    const option = (text: string, on: () => boolean, toggle: () => void) => {
        const button = Button()
        button.addClass(style.option)
        const mark = Div()
        mark.addClass(style.optionMark)
        button.append(mark)
        button.append(Div(text))
        button.on('click', toggle)
        syncers.push(() => {
            const marked = on()
            mark.html(marked ? CHECK_SVG : '')
            button.toggleClass(style.marked, marked)
            button.attr('aria-label', `${marked ? 'حذف' : 'افزودن'} ${text}`)
        })
        options.append(button)
    }

    option(`همهٔ ${many}`, () => selection.all(), () => selection.setAll(!selection.all()))

    for (const region of codes) {
        option(nameOf(region),
            () => selection.has(region),
            () => selection.set(region, !selection.has(region)))
    }

    const tops = country.regions.filter(r => !r.parent).length
    if (tops > codes.length) {
        const note = Div(`بقیهٔ ${many} تعطیل جداگانه‌ای فراتر از تعطیلات سراسری ندارند.`)
        note.addClass(style.optionsNote)
        options.append(note)
    }

    return wrap
}
